import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/hooks/useLanguage";

interface PageSEOProps {
  title?: string;
  description?: string;
  structuredData?: Record<string, unknown>;
}

const SITE_NAME = "Geposit";
const BASE_URL = "https://geposit.se";

const SUPPORTED_LANGS = ["sv", "en", "no", "da", "fi"];

export function PageSEO({ title, description, structuredData }: PageSEOProps) {
  const { pathname } = useLocation();
  const { lang } = useLanguage();
  const { t } = useTranslation();
  const tagline = t("seo.tagline", "Geografisk kontroll över din verksamhet");
  const defaultDescription = t("seo.home", "Geposit erbjuder adressvalidering, adresstvätt och geografisk områdesindelning för hela Norden.");
  const fullTitle = title ? `${title} – ${SITE_NAME}` : `${SITE_NAME} – ${tagline}`;
  const canonical = `${BASE_URL}${pathname === "/" ? "/sv" : pathname}`;
  const desc = description || defaultDescription;

  // Path without language prefix for hreflang alternates
  const pathWithoutLang = pathname.replace(/^\/(sv|en|no|da|fi)/, "") || "/";

  return (
    <Helmet>
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={canonical} />

      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />

      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />

      {/* hreflang alternates */}
      {SUPPORTED_LANGS.map((l) => (
        <link
          key={l}
          rel="alternate"
          hrefLang={l}
          href={`${BASE_URL}/${l}${pathWithoutLang === "/" ? "" : pathWithoutLang}`}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${BASE_URL}/sv${pathWithoutLang === "/" ? "" : pathWithoutLang}`} />

      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify({ "@context": "https://schema.org", ...structuredData })}
        </script>
      )}
    </Helmet>
  );
}
